import { Button, Card } from "pepsico-ds";
import PropTypes from "prop-types";
import { getDateWithFormat } from "../../utils/commons";
import {
  CODE_GROUP_CATEGORY,
  CODE_GROUP_CREATION_TYPE,
  CODE_GROUP_CREATION_TYPE_LABELS,
  CODE_GROUP_LIFE_CYCLE_STATUS,
} from "../../utils/constants";
import { codeGroupActionButton } from "./codeGroupActionButton";
import "./codeGroupsCard.scss";

const getDisplayValue = (value) =>
  value !== undefined && value !== null ? value : "-";

const formatCategory = (rawCategory) =>
  rawCategory?.toUpperCase() === CODE_GROUP_CATEGORY.SUPPORT ? "Support" : "Default";

const formatDateRange = (startDate, endDate, timezone) => {
  const formattedStartDate = startDate
    ? getDateWithFormat(startDate, timezone)
    : "-";
  const formattedEndDate = endDate ? getDateWithFormat(endDate, timezone) : "-";
  const availableDates = [formattedStartDate, formattedEndDate].filter(
    (date) => date !== "-"
  );

  return {
    formattedStartDate,
    formattedEndDate,
    formattedDateRange: availableDates.length > 0 ? availableDates.join(" - ") : "-",
  };
};

const formatCount = (count) =>
  typeof count === "number" ? count.toLocaleString() : count;

const formatMaxUsageCount = ({ codesMaxUsageCount, hasUnlimitedCodes }) => {
  if (hasUnlimitedCodes) {
    return codesMaxUsageCount > 0
      ? `${codesMaxUsageCount.toLocaleString()} + Unlimited`
      : "Unlimited";
  }
  return codesMaxUsageCount?.toLocaleString() ?? "-";
};

const getCardActions = (group, handleActionButtons, downloadPermission) => {
  const orderedActions = ["view", "edit", "copy", "export"];

  return codeGroupActionButton(group, handleActionButtons, downloadPermission)
    .filter((action) => orderedActions.includes(action.action))
    .sort(
      (a, b) => orderedActions.indexOf(a.action) - orderedActions.indexOf(b.action)
    );
};

const CodeGroupsCard = ({
  group,
  currentProgramTimezone,
  downloadPermission,
  handleActionButtons,
  isSelected,
  onSelect,
}) => {
  if (!group) {
    return "No Data Found";
  }

  const allowedStatuses = [
    CODE_GROUP_LIFE_CYCLE_STATUS.DRAFT.toLowerCase(),
    CODE_GROUP_LIFE_CYCLE_STATUS.INACTIVE.toLowerCase(),
  ];
  const basicSetup = group.basicSetup || {};
  const producerPackaging = group.producerPackaging || {};
  const producerCompany = producerPackaging.producerCompany || "-";
  const creationType =
    group.creationType || CODE_GROUP_CREATION_TYPE.SYSTEM_GENERATED;
  const codesGeneratedCount = getDisplayValue(group.codesGeneratedCount);
  const codesUsedCount = getDisplayValue(group.codesUsedCount);
  const compositeStatus = group.compositeStatus || "-";
  const codeGroupName = basicSetup.codeGroupName || "-";
  const codeActiveStartDate = basicSetup.codeActiveStartDate || null;
  const codeActiveEndDate = basicSetup.codeActiveEndDate || null;
  const rawCategory = group.category || CODE_GROUP_CATEGORY.DEFAULT;

  const creationTypeLabel = CODE_GROUP_CREATION_TYPE_LABELS[creationType] || "-";
  const isUploadedType = creationType === CODE_GROUP_CREATION_TYPE.PRE_GENERATED;
  const hasMultiFileDownloadEnabled =
    creationType === CODE_GROUP_CREATION_TYPE.SYSTEM_GENERATED;
  const category = formatCategory(rawCategory);
  const canSelect = allowedStatuses.includes(group.compositeStatus?.toLowerCase());
  const { formattedStartDate, formattedEndDate, formattedDateRange } =
    formatDateRange(codeActiveStartDate, codeActiveEndDate, currentProgramTimezone);
  const formattedCodeCount = formatCount(codesGeneratedCount);
  const formattedCodesUsedCount = formatCount(codesUsedCount);
  const formattedMaxUsageCount = formatMaxUsageCount(group);
  const cardActions = getCardActions(group, handleActionButtons, downloadPermission);
  const normalizedCategory = category.toLowerCase().replace(/\s/g, "-");
  const normalizedStatus = compositeStatus
    ? compositeStatus.toLowerCase().replace(/\s/g, "-")
    : "unknown";

  const isFileReadyToDownload = isUploadedType && group?.fileDetails?.length > 0;

  const handleDownloadFile = (file) => {
    if (file?.url) {
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadFiles = () => {
    if (group?.fileDetails?.length > 0) {
      group.fileDetails.forEach((file) => {
        handleDownloadFile(file);
      });
    }
  };

  return (
    <Card
      icon=""
      ellipsis=""
      style={{
        padding: "0px",
      }}
      className={`code-group-card ${isSelected ? "selected" : ""}`}
    >
      <div className="code-group-card-container">
        <div className="code-group-card-details">
          <div className="code-group-header">
            {canSelect ? (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => onSelect(group.codeGroupId, e.target.checked)}
              />
            ) : null}
            <div className="code-group-title">
              <h3 className="code-group-name">{codeGroupName}</h3>
            </div>
            <div className="code-group-badges">
              <span
                className={`code-creation-type-badge ${creationType.toLowerCase()}`}
              >
                {creationTypeLabel}
              </span>
              <span className={`code-category-badge ${normalizedCategory}`}>
                {category}
              </span>
              <span className={`code-status-badge ${normalizedStatus}`}>
                {compositeStatus}
              </span>
            </div>
          </div>
          <div className="code-group-separator" />
          <div className="code-group-info">
            {isUploadedType ? (
              <div className="code-group-info-grid">
                <div className="info-item">
                  <span className="info-label">Unique Codes</span>
                  <span className="info-value">{formattedCodeCount}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Max Usage</span>
                  <span className="info-value">{formattedMaxUsageCount}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Used Count</span>
                  <span className="info-value">{formattedCodesUsedCount}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Start Date</span>
                  <span className="info-value">{formattedStartDate}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">End Date</span>
                  <span className="info-value">{formattedEndDate}</span>
                </div>
              </div>
            ) : (
              <div className="code-group-info-grid">
                <div className="info-item">
                  <span className="info-label">Unique Codes</span>
                  <span className="info-value">{formattedCodeCount}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Active Start and End Date</span>
                  <span className="info-value">{formattedDateRange}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Used Count</span>
                  <span className="info-value">{formattedCodesUsedCount}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Producer Company</span>
                  <span className="info-value">{producerCompany}</span>
                </div>
              </div>
            )}
          </div>
          <div className="code-group-separator" />
          <div className="code-group-actions">
            {cardActions.map((button) =>
              hasMultiFileDownloadEnabled && button.action === "export" ? (
                <Button
                  variant="primary"
                  size="medium"
                  onClick={() => handleActionButtons("downloadMultipleFile", group)}
                  key={button.action}
                >
                  Download
                </Button>
              ) : (
                <button
                  key={button.action}
                  type="button"
                  className={`code-group-action-btn ${
                    button.action === "export" ? "primary" : ""
                  }`}
                  disabled={button.action === "export" && !isFileReadyToDownload}
                  onClick={
                    button.action === "export" && isUploadedType
                      ? handleDownloadFiles
                      : button.onClick
                  }
                >
                  {button.action === "export" && isUploadedType
                    ? "Download"
                    : button.label || button.action}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

CodeGroupsCard.propTypes = {
  group: PropTypes.shape({
    basicSetup: PropTypes.shape({
      codeGroupName: PropTypes.string,
      codeActiveStartDate: PropTypes.string,
      codeActiveEndDate: PropTypes.string,
    }),
    producerPackaging: PropTypes.shape({
      producerCompany: PropTypes.string,
    }),
    codesGeneratedCount: PropTypes.number,
    codesUsedCount: PropTypes.number,
    administrationStatus: PropTypes.string,
    compositeStatus: PropTypes.string,
    category: PropTypes.string,
    sampleCode: PropTypes.string,
    codeSample: PropTypes.string,
    exportFile: PropTypes.string,
    codeGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    creationType: PropTypes.string,
    codeSpecifications: PropTypes.shape({
      codeLength: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      excludeCharacters: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      excludeCharacterValues: PropTypes.arrayOf(PropTypes.string),
      sampleCode: PropTypes.string,
    }),
  }),
  currentProgramTimezone: PropTypes.string,
  downloadPermission: PropTypes.bool,
  handleActionButtons: PropTypes.func,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
};

export default CodeGroupsCard;
